import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Tag, Image, ChevronRight, ChevronLeft } from 'lucide-react';
import type { Room } from '../types'; // Assuming Room type is in ../types or FloorPlanCreator
import PhotoCapture from './PhotoCapture';
import PhotoGalleryModal from './PhotoGalleryModal';
import type { PhotoData } from '../services/CameraService';
import { ROOM_NAME_MAPPINGS } from '../utils/roomNameUtils';

export interface DisplayablePhotoData extends PhotoData {
  displayId: string;
  dataUrl: string;
  fileName?: string;
  url?: string;
  uploading?: boolean;
  error?: string;
}

interface RoomLabelPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roomId: string, label: string, wallImages?: DisplayablePhotoData[]) => void;
  roomToLabel: Room | null;
  existingRooms: Room[]; // To help suggest next numbers like BR1, BR2
  isFullscreen?: boolean; // Added to handle fullscreen z-index
  skipWallImages?: boolean; // Skip wall images step for estate/energy floorplans
  multiRoomMode?: boolean; // If true, showing multi-room AR scan labeling
  currentRoomNumber?: number; // Current room number in multi-room sequence (1-indexed)
  totalRoomCount?: number; // Total rooms in multi-room scan
  isEstate?: boolean; // Estate mode - show full room names instead of abbreviations
}

const PREDEFINED_LABELS = [
  { name: 'Bedroom', prefix: 'BR', default: 'BR1' },
  { name: 'Bathroom', value: 'BTH' },
  { name: 'Living Room', prefix: 'LR', default: 'LR1' },
  { name: 'Hallway', prefix: 'H', default: 'H1' },
  { name: 'Landing', prefix: 'L', default: 'L1' },
  { name: 'Kitchen/Diner', value: 'K/D' },
  { name: 'Kitchen', value: 'K' },
  { name: 'Water Closet', value: 'W/C' },
  { name: 'Cupboard', value: 'CPD' },
  // Add more as needed
];

const RoomLabelPrompt: React.FC<RoomLabelPromptProps> = ({
  isOpen,
  onClose,
  onSave,
  roomToLabel,
  existingRooms,
  isFullscreen = false,
  skipWallImages = false,
  multiRoomMode = false,
  currentRoomNumber = 1,
  totalRoomCount = 1,
  isEstate = false,
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1); // Step 1: Label, Step 2: Wall Images
  const [label, setLabel] = useState('');
  const [wallImages, setWallImages] = useState<DisplayablePhotoData[]>([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    if (roomToLabel) {
      // Suggest a default label or the next available prefix
      const firstOption = PREDEFINED_LABELS[0];
      if (firstOption.prefix) {
        let nextNumber = 1;
        let suggestedLabel = `${firstOption.prefix}${nextNumber}`;
        // eslint-disable-next-line no-loop-func
        while (existingRooms.some(r => r.name === suggestedLabel)) {
          nextNumber++;
          suggestedLabel = `${firstOption.prefix}${nextNumber}`;
        }
        setLabel(suggestedLabel);
      } else {
        setLabel(firstOption.default || firstOption.value || 'New Room');
      }
    } else {
      setLabel(''); // Reset when no room is to be labeled
      setCurrentStep(1); // Reset to first step
      setWallImages([]); // Clear wall images
    }
  }, [roomToLabel, isOpen]);

  const handleNextStep = () => {
    if (label.trim()) {
      if (skipWallImages) {
        // For estate/energy floorplans, skip wall images and save directly
        handleSkipImages();
      } else {
        setCurrentStep(2);
      }
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSave = () => {
    if (roomToLabel && label.trim()) {
      onSave(roomToLabel.id, label.trim(), wallImages.length > 0 ? wallImages : undefined);
      // Reset state
      setCurrentStep(1);
      setWallImages([]);
    }
  };

  const handleSkipImages = () => {
    if (roomToLabel && label.trim()) {
      onSave(roomToLabel.id, label.trim());
      // Reset state
      setCurrentStep(1);
      setWallImages([]);
    }
  };

  const handlePhotosCaptured = (capturedPhotos: PhotoData[]) => {
    const newPhotos: DisplayablePhotoData[] = capturedPhotos.map((p, idx) => {
      const displayId = `wall-${Date.now()}-${idx}`;
      const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
      return {
        ...p,
        displayId,
        dataUrl,
        uploading: false,
        fileName: undefined,
        url: undefined,
        error: undefined
      };
    });
    setWallImages(prev => [...prev, ...newPhotos]);
  };

  const handleDeletePhoto = (displayId: string) => {
    setWallImages(prev => prev.filter(p => p.displayId !== displayId));
  };

  const handleUpdateNote = (photoId: string, note: string) => {
    setWallImages(prev => prev.map(p => 
      p.displayId === photoId ? { ...p, note } as DisplayablePhotoData : p
    ));
  };

  const handleOpenGallery = () => {
    if (wallImages.length > 0) {
      setIsGalleryOpen(true);
    }
  };

  const handlePredefinedClick = (selected: { name: string; prefix?: string; value?: string; default?: string }) => {
    if (selected.prefix) {
      let nextNumber = 1;
      let suggestedLabel = `${selected.prefix}${nextNumber}`;
      // eslint-disable-next-line no-loop-func
      while (existingRooms.some(r => r.name === suggestedLabel && r.id !== roomToLabel?.id)) {
        nextNumber++;
        suggestedLabel = `${selected.prefix}${nextNumber}`;
      }
      setLabel(suggestedLabel);
    } else {
      setLabel(selected.value || selected.default || 'New Room');
    }
  };

  if (!isOpen || !roomToLabel) {
    return null;
  }

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-lg border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 rounded-full p-2">
                  {currentStep === 1 ? <Tag className="w-5 h-5 text-white" /> : <Image className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">
                    {currentStep === 1 
                      ? (multiRoomMode ? `Room ${currentRoomNumber} Label` : 'Label Room')
                      : 'Add Wall Images'}
                  </h2>
                  <p className="text-emerald-100 text-sm">
                    {currentStep === 1 
                      ? (multiRoomMode 
                          ? `Label room ${currentRoomNumber} of ${totalRoomCount}` 
                          : 'Choose or enter a room label')
                      : 'Capture images of all 4 walls (optional)'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
            {/* Step Indicator */}
            <div className="flex items-center justify-center mt-4 space-x-2 relative z-10">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                currentStep === 1 ? 'bg-white text-emerald-600' : 'bg-white/30 text-white'
              }`}>
                1
              </div>
              <div className={`w-12 h-0.5 transition-all duration-200 ${
                currentStep === 2 ? 'bg-white' : 'bg-white/30'
              }`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                currentStep === 2 ? 'bg-white text-emerald-600' : 'bg-white/30 text-white'
              }`}>
                2
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-cyan-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50/50 to-white max-h-[60vh] overflow-y-auto">
          {currentStep === 1 ? (
            // Step 1: Room Label
            <>
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                  Room Label
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && label.trim()) {
                      handleNextStep();
                    }
                  }}
                  placeholder="e.g., Bedroom 1, Living Room"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900 placeholder-gray-500"
                  readOnly
                  onFocus={(e) => e.target.blur()}
                />
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  Quick Suggestions
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {PREDEFINED_LABELS.map((pLabel) => {
                    // Show full names in estate mode
                    const displayName = isEstate && (pLabel.value || pLabel.default) 
                      ? (ROOM_NAME_MAPPINGS[pLabel.value || pLabel.default || ''] || pLabel.name)
                      : pLabel.name;
                    const exampleLabel = pLabel.prefix ? `${pLabel.prefix}1` : pLabel.value;
                    const displayExample = isEstate && exampleLabel
                      ? (ROOM_NAME_MAPPINGS[exampleLabel] || exampleLabel)
                      : exampleLabel;
                    
                    return (
                      <button
                        key={pLabel.name}
                        onClick={() => handlePredefinedClick(pLabel)}
                        className="px-3 py-2.5 text-sm bg-gradient-to-r from-gray-50 to-teal-50/30 text-gray-700 rounded-xl hover:from-teal-50 hover:to-teal-100/50 hover:shadow-md transition-all duration-200 border border-gray-200/50 hover:border-teal-300 font-medium text-left"
                      >
                        <div className="truncate">
                          {displayName}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {pLabel.prefix ? `e.g. ${displayExample}` : displayExample}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            // Step 2: Wall Images
            <>
              <div className="text-center py-2">
                <p className="text-sm text-gray-600 mb-4">
                  Room <span className="font-bold text-emerald-600">{label}</span>
                </p>
              </div>

              <div>
                <label className="flex items-center text-sm font-semibold text-gray-900 mb-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                  Wall Images
                  <span className="ml-2 text-xs font-normal text-gray-500">(Capture all 4 walls)</span>
                </label>
                
                <PhotoCapture
                  onPhotosCaptured={handlePhotosCaptured}
                  maxPhotos={999}
                  buttonLabel="Capture Wall Images"
                  buttonClass="w-full px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                />
              </div>

              {wallImages.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Captured Images ({wallImages.length})
                  </h4>
                  <div 
                    onClick={handleOpenGallery}
                    className="relative w-full h-32 rounded-xl border-2 border-gray-300 overflow-hidden cursor-pointer hover:border-emerald-500 transition-all group"
                  >
                    <img
                      src={wallImages[0].dataUrl}
                      alt="Wall Image"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-center pb-3">
                      <span className="text-white font-bold text-lg drop-shadow-lg">
                        {wallImages.length} Image{wallImages.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-emerald-600/0 group-hover:bg-emerald-600/10 transition-all" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white rounded-b-2xl min-h-[88px]">
          {currentStep === 1 ? (
            // Step 1 Footer
            <>
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Cancel
              </button>
              <button
                onClick={handleNextStep}
                disabled={!label.trim()}
                className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg ${
                  label.trim()
                    ? 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl hover:scale-105 ring-2 ring-emerald-200/50'
                    : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                }`}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </>
          ) : (
            // Step 2 Footer
            <>
              <button
                onClick={handlePreviousStep}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-2 h-[42px]"
              >
                <ChevronLeft size={16} />
                <span>Back</span>
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={handleSkipImages}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md h-[42px]"
                >
                  Skip
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 ring-2 ring-emerald-200/50 flex items-center space-x-2 h-[42px] whitespace-nowrap"
                >
                  <Tag size={16} />
                  <span>Save{wallImages.length > 0 ? ` (${wallImages.length})` : ''}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {isGalleryOpen && (
        <PhotoGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          photos={wallImages.map(img => ({
            id: img.displayId,
            url: img.url,
            localPath: img.dataUrl,
            fileName: img.fileName,
            uploading: img.uploading,
            error: img.error,
            note: (img as any).note
          }))}
          sectionTitle="Wall Images"
          onDeletePhoto={handleDeletePhoto}
          onUpdateNote={handleUpdateNote}
          zIndex={10000000} // High z-index to appear above RoomLabelPrompt
        />
      )}
    </>
  );
};

export default RoomLabelPrompt;